<template>
    <layout-authenticated>
        <v-breadcrumbs :items="breadcrumb"></v-breadcrumbs>
        <h1 class="mb-3">{{ $t('approval.edit_order') }}</h1>
        <template v-if="order !== null">
            <h3 class="grey--text mb-3">{{ $t('approval.add_product_quickly') }}</h3>
            <add-product-quickly-component :user-id="userId" :warehouse-id="warehouseId" store-module="editOrder"/>
            <h3 class="grey--text mb-3">{{ $t('approval.products_in_order') }}</h3>
            <shopping-cart-component
                :user-id="userId"
                :warehouse-id="warehouseId"
                :stock-allowance="hasActiveReservation"
                class="mb-3"
                store-module="editOrder"
            />
            <h3 class="grey--text mb-3">{{ $t('approval.add_recipient') }}</h3>
            <add-recipient-component
                :user-id="userId"
                :warehouse-id="warehouseId"
                store-module="editOrder"
                single-recipient
                @recipient-selected="onRecipientSelected"
            />
            <h3 class="grey--text mb-3">{{ $t('approval.recipients') }}</h3>
            <recipients-table @isValid="onIsValid" :user-id="userId" :warehouse-id="warehouseId" store-module="editOrder"/>
            <h3 class="grey--text my-3">{{ $t('summary.order_message') }}</h3>
            <v-textarea
                v-model="form.message"
                :label="$t('summary.message')"
                rows="3"
                outlined
                dense
            />
            <v-btn
                @click="onSave(false)"
                :disabled="isBusy || recipientsCount !== 1 || productsCount === 0 || !checkProductsCount || !recipientsValid"
                :loading="activeSaveAction === 'save'"
                color="primary"
                class="mt-3 mr-3"
            >
                <v-icon left>mdi-content-save-outline</v-icon>
                {{ $t('approval.save') }}
            </v-btn>
            <v-btn
                @click="onSave(true)"
                :disabled="isBusy || recipientsCount !== 1 || productsCount === 0 || !checkProductsCount || !recipientsValid"
                :loading="activeSaveAction === 'saveAndApprove'"
                color="warning"
                class="mt-3"
            >
                <v-icon left>mdi-checkbox-marked-circle-plus-outline</v-icon>
                {{ $t('approval.save_and_approve') }}
            </v-btn>
        </template>
    </layout-authenticated>
</template>

<script>
import LayoutAuthenticated from "@/layouts/LayoutAuthenticated";
import SnackbarMixin from "@/mixins/SnackbarMixin";
import {API} from "@/api/api";
import AddProductQuicklyComponent from "@/components/user/shoppingCart/AddProductQuicklyComponent";
import ShoppingCartComponent from "@/components/user/shoppingCart/ShoppingCartComponent";
import AddRecipientComponent from "@/components/user/shoppingCart/AddRecipientComponent";
import RecipientsTable from "@/components/user/recipients/RecipientsTable";
import ShoppingCartMixin from "@/mixins/ShoppingCartMixin";
import {
    aggregateProducts,
    deepFreeze,
    findUnavailableProducts,
    formatUnavailableProductsAlert,
    snapshotProducts,
    snapshotRecipients,
    snapshotShoppingCartData
} from "@/utils/orderAvailability";

export default {
    name: "EditOrder",
    mixins: [SnackbarMixin, ShoppingCartMixin],
    components: {
        RecipientsTable,
        AddRecipientComponent,
        ShoppingCartComponent,
        AddProductQuicklyComponent,
        LayoutAuthenticated
    },
    data: () => ({
        // loading
        isBusyOrder: false,
        activeSaveAction: null,
        // data
        order: null,
        // validation
        recipientsValid: false,
        // form
        form: {
            message: null
        }
    }),
    computed: {
        isBusy() {
            return this.isBusyOrder || this.activeSaveAction !== null;
        },
        userId() {
            return this.order.user_id;
        },
        warehouseId() {
            return this.order.warehouse_id;
        },
        orderId() {
            return this.$route.params.orderId;
        },
        recipientsCount() {
            return this.getRecipientsInCart(this.userId, this.warehouseId, "editOrder").length;
        },
        productsCount() {
            return this.getProductsInCart(this.userId, this.warehouseId, "editOrder").length;
        },
        checkProductsCount() {
            return this.getProductsInCart(this.userId, this.warehouseId, "editOrder")
                .reduce((carry, product) => {
                    const quantity = Number(product.quantity);
                    const inStock = Number(product.in_stock);
                    const originalQuantity = this.hasActiveReservation
                        ? Number(product.original_quantity || 0)
                        : 0;
                    const availableQuantity = inStock + originalQuantity;
                    return carry
                        && Number.isSafeInteger(quantity)
                        && quantity > 0
                        && Number.isSafeInteger(inStock)
                        && inStock >= 0
                        && Number.isSafeInteger(originalQuantity)
                        && originalQuantity >= 0
                        && Number.isSafeInteger(availableQuantity)
                        && quantity <= availableQuantity;
                }, true);
        },
        hasActiveReservation() {
            return this.order.reservation_status === "active"
                && this.order.blocking_order_external_id != null;
        },
        breadcrumb() {
            return [
                {
                    text: this.$t('app.name'),
                    href: '/'
                },
                {
                    text: this.$t('approval.approval'),
                    href: '/approver/approval'
                },
                {
                    text: this.$t('approval.edit_order'),
                    href: '/approver/orders/' + this.orderId,
                    disabled: true
                }
            ];
        }
    },
    methods: {
        async onSave(approve) {
            if (this.isBusy) {
                return;
            }

            this.activeSaveAction = approve ? "saveAndApprove" : "save";
            try {
                const orderId = this.orderId;
                const userId = this.userId;
                const warehouseId = this.warehouseId;
                const products = snapshotProducts(
                    this.getProductsInCart(userId, warehouseId, "editOrder"),
                    {includeOriginalQuantity: true}
                );
                const recipients = snapshotRecipients(
                    this.getRecipientsInCart(userId, warehouseId, "editOrder")
                );
                if (recipients.length !== 1) {
                    return;
                }

                const message = this.form.message;
                const shoppingCartData = snapshotShoppingCartData(
                    userId,
                    warehouseId,
                    products,
                    recipients
                );
                const recipient = recipients[0];
                const payload = deepFreeze({
                    // address & contact info
                    buyer_external_id: recipient.buyer_id,
                    address_external_id: recipient.address_id,
                    phone: recipient.phone,
                    email: recipient.email,
                    // warehouse
                    warehouse_id: warehouseId,
                    // ordered items
                    items: products.map(product => ({
                        name: product.name,
                        external_id: product.external_id,
                        brand: product.brand,
                        model: product.model,
                        image_url: product.image_url,
                        quantity: product.quantity,
                    })),
                    // optional message
                    message,
                    // shopping cart raw data
                    shopping_cart_data: JSON.stringify(shoppingCartData)
                });
                const unavailableProducts = await findUnavailableProducts(
                    warehouseId,
                    aggregateProducts([products]),
                    API
                );

                if (unavailableProducts.length > 0) {
                    window.alert(formatUnavailableProductsAlert(
                        this.$t("summary.check_availability_failure"),
                        unavailableProducts
                    ));
                    return;
                }

                await API.put("/api/orders/" + orderId, payload);
                this.snackbarSuccess(this.$t("snackbar.order.updated"));
                this.$store.commit("editOrder/reset");
                if (approve) {
                    await this.$router.push({name: "order", params: {orderId}});
                } else {
                    await this.$router.push({name: "approval"});
                }
            } catch (error) {
                this.snackbarError(
                    typeof error === "string"
                        ? error
                        : (error.message || this.$t("snackbar.unknown_error"))
                );
            } finally {
                this.activeSaveAction = null;
            }
        },
        onIsValid(valid) {
            this.recipientsValid = valid;
        },
        onRecipientSelected(recipient) {
            this.recipientsValid = Boolean(recipient.phone && recipient.email);
        },
        fetchOrder() {
            this.isBusyOrder = true;
            API.get("/api/orders/" + this.orderId)
                .then(response => {
                    this.order = response.data;
                    this.form.message = response.data.message;
                    const storedOrderId = this.$store.getters["editOrder/getOrderId"];
                    const sourceData = this.orderId != storedOrderId
                        ? JSON.parse(response.data.shopping_cart_data)
                        : this.$store.getters["editOrder/getData"];
                    this.$store.commit("editOrder/setOrderId", response.data.id);
                    this.$store.commit("editOrder/setData", {
                        data: sourceData,
                        userId: response.data.user_id,
                        warehouseId: response.data.warehouse_id,
                        addressExternalId: response.data.address.external_id,
                        originalProducts: this.hasActiveReservation ? response.data.products : []
                    });
                })
                .catch(error => {
                    this.snackbarError(error);
                })
                .finally(() => {
                    this.isBusyOrder = false;
                });
        }
    },
    created() {
        this.fetchOrder()
    }
}
</script>
